import React, { useState, useEffect } from 'react';
import { ChevronRight, AlertTriangle } from 'lucide-react';
import { formatShortDate } from '../../utils/dateFormatter';

const AVATAR_COLORS = [
  'bg-rose-50 text-rose-600', 'bg-sky-50 text-sky-600',
  'bg-amber-50 text-amber-600', 'bg-violet-50 text-violet-600',
  'bg-teal-50 text-teal-600', 'bg-fuchsia-50 text-fuchsia-600',
];
const getColor = (name) => {
  let h = 0;
  for (let i = 0; i < (name || '').length; i++) h = name.charCodeAt(i) + ((h << 5) - h);
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length];
};
const getInitials = (name) => {
  if (!name) return '?';
  const p = name.trim().split(/\s+/);
  return p.length >= 2 ? (p[0][0] + p[1][0]).toUpperCase() : p[0].substring(0, 2).toUpperCase();
};

const getStatusLabel = (status, booking) => {
  // If booking has deposit paid, show "Booked" regardless of inquiry.status
  if (booking?.deposit_paid && !['completed', 'cancelled', 'declined'].includes(status)) {
    return { text: 'Booked', cls: 'text-emerald-600' };
  }
  switch (status) {
    case 'pending': return { text: 'Awaiting reply', cls: 'text-gray-400' };
    case 'reviewing': return { text: 'In discussion', cls: 'text-blue-500' };
    case 'proposal_sent': return { text: 'Proposal ready', cls: 'text-amber-600 font-medium' };
    case 'accepted': return { text: 'Accepted', cls: 'text-emerald-600' };
    case 'in_progress': return { text: 'In progress', cls: 'text-emerald-600' };
    case 'deposit_paid': return { text: 'Booked', cls: 'text-emerald-600' };
    case 'completed': return { text: 'Completed', cls: 'text-gray-400' };
    case 'declined': return { text: 'Declined', cls: 'text-red-400' };
    case 'cancelled': return { text: 'Cancelled', cls: 'text-red-400' };
    default: return { text: status || 'Sent', cls: 'text-gray-400' };
  }
};

const formatRelativeTime = (dateStr) => {
  if (!dateStr) return '';
  const now = new Date();
  const d = new Date(dateStr);
  const diffMs = now - d;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return 'now';
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const MessageConversationCard = ({ inquiry, booking, onClick }) => {
  const hasUnread = (inquiry.unread_count || 0) > 0;
  const hasPendingChange = inquiry.has_pending_change_request;
  const statusInfo = getStatusLabel(inquiry.status, booking);
  const lastActivity = inquiry.updated_at || inquiry.created_at;
  // Closed/historical relationship — used to render a small "Closed" pill
  // so the user understands BEFORE tapping that this thread is read-only.
  // No routing change; existing per-section grouping ("Past") still applies.
  const isClosed = ['cancelled', 'declined', 'completed'].includes(inquiry.status);

  // Track image load failure so a broken/expired URL falls back to initials
  // (browser default broken-image glyph would otherwise render).
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [inquiry.vendor_image]);
  const showImg = inquiry.vendor_image && !imgFailed;

  return (
    <button
      className={`w-full text-left flex items-center gap-4 px-4 py-4 rounded-2xl transition-all duration-150 active:scale-[0.99]
        ${hasPendingChange
          ? 'bg-amber-50/40 hover:bg-amber-50/60'
          : hasUnread
            ? 'bg-emerald-50/40 hover:bg-emerald-50/60'
            : 'bg-white hover:bg-gray-50/80'
        }`}
      onClick={() => onClick(inquiry)}
      data-testid={`chat-card-${inquiry.id}`}
    >
      {/* Avatar — vendor business/owner photo with initials fallback */}
      {showImg ? (
        <div className="shrink-0 w-12 h-12 rounded-full overflow-hidden bg-gray-100">
          <img
            src={inquiry.vendor_image}
            alt={inquiry.vendor_name || ''}
            className="w-full h-full object-cover"
            onError={() => setImgFailed(true)}
            data-testid={`chat-avatar-img-${inquiry.id}`}
          />
        </div>
      ) : (
        <div
          className={`shrink-0 w-12 h-12 rounded-full flex items-center justify-center font-semibold text-sm ${getColor(inquiry.vendor_name)}`}
          data-testid={`chat-avatar-initials-${inquiry.id}`}
        >
          {getInitials(inquiry.vendor_name)}
        </div>
      )}

      {/* Content */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-0.5">
          <div className="flex items-center gap-1.5 min-w-0">
            <span className={`font-semibold truncate text-sm ${hasUnread ? 'text-gray-950' : 'text-gray-800'}`}>
              {inquiry.vendor_name}
            </span>
            {isClosed && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-1.5 py-0.5 rounded-full shrink-0"
                data-testid={`chat-closed-badge-${inquiry.id}`}
              >
                Closed
              </span>
            )}
          </div>
          <span className="text-[11px] text-gray-400 shrink-0 tabular-nums">
            {formatRelativeTime(lastActivity)}
          </span>
        </div>
        <p className="text-[13px] text-gray-500 truncate leading-snug">
          {inquiry.event_type}
          <span className="mx-1.5 text-gray-200">&middot;</span>
          {formatShortDate(inquiry.event_date)}
        </p>
        <div className="flex items-center justify-between mt-1">
          <div className="flex items-center gap-1.5 min-w-0">
            <p className={`text-xs truncate ${statusInfo.cls}`}>
              {statusInfo.text}
            </p>
            {hasPendingChange && (
              <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded-full shrink-0" data-testid="change-requested-badge">
                <AlertTriangle className="w-2.5 h-2.5" />
                Change Sent
              </span>
            )}
          </div>
          {hasUnread && (
            <span className="inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold bg-emerald-600 text-white shrink-0 ml-2">
              {inquiry.unread_count}
            </span>
          )}
        </div>
      </div>

      <ChevronRight className="w-4 h-4 text-gray-300 shrink-0 ml-1" />
    </button>
  );
};

export default MessageConversationCard;
